import { useCallback, useEffect, useState } from "react";
import type { PrimeStyleClassNames } from "../../types";
import type { TranslateFn } from "../../i18n";
import { cx } from "../utils/classnames";

// Radius of the circular progress ring. Must match the circumference used
// in PrimeStyleTryonInner's ticker (2 * PI * 38 ≈ 238.76).
const RING_RADIUS = 38;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Engaging try-on processing screen — 65/35 split.
 *  Left  (~65%): user's photo + scanning overlay
 *  Right (~35%): big % ring → "WHILE YOU WAIT" cards (style tip /
 *                garment spotlight / AI fact) → cancel link.
 *
 * Distracts the user from the 25s wait by feeding them branded micro-
 * content tied to their actual purchase context.
 */
export function ProcessingView({
  previewUrl,
  progressRef,
  progressBarRef,
  progressTextRef,
  progressStatusRef,
  progressEtaRef,
  progressRingRef,
  productTitle,
  productMaterial,
  productDescription,
  onCancel,
  cn,
  t,
}: {
  previewUrl: string | null;
  progressRef: React.MutableRefObject<number>;
  progressBarRef: React.MutableRefObject<HTMLDivElement | null>;
  progressTextRef: React.MutableRefObject<HTMLSpanElement | null>;
  progressStatusRef: React.MutableRefObject<HTMLDivElement | null>;
  progressEtaRef: React.MutableRefObject<HTMLSpanElement | null>;
  progressRingRef: React.MutableRefObject<SVGCircleElement | null>;
  productTitle?: string;
  productMaterial?: string;
  productDescription?: string;
  onCancel?: () => void;
  cn: PrimeStyleClassNames;
  t: TranslateFn;
}) {
  // Sync refs to current progress on mount/re-render
  const barCb = useCallback((el: HTMLDivElement | null) => { (progressBarRef as any).current = el; if (el) el.style.width = `${Math.round(progressRef.current)}%`; }, []);
  const pctCb = useCallback((el: HTMLSpanElement | null) => { (progressTextRef as any).current = el; if (el) el.textContent = `${Math.round(progressRef.current)}%`; }, []);
  const statusCb = useCallback((el: HTMLDivElement | null) => { (progressStatusRef as any).current = el; }, []);
  const etaCb = useCallback((el: HTMLSpanElement | null) => { (progressEtaRef as any).current = el; }, []);
  const ringCb = useCallback((el: SVGCircleElement | null) => {
    (progressRingRef as any).current = el;
    if (el) {
      const offset = RING_CIRCUMFERENCE * (1 - Math.round(progressRef.current) / 100);
      el.style.strokeDashoffset = String(offset);
    }
  }, []);

  // Rotating "AI fact" so the bottom card isn't static for 25s.
  const aiFacts = [
    t("Our model is analyzing 150+ body landmarks for your size suggestion"),
    t("Calibrating fabric drape against your body proportions"),
    t("Cross-checking fit against millions of garment patterns"),
    t("Rendering shadows and highlights to match your photo's lighting"),
  ];
  const styleTip = (() => {
    const t1 = t("Pair this with neutral tones for a balanced look");
    const t2 = t("Layer with a fitted base for sharper silhouette");
    const t3 = t("Cuff once for a relaxed everyday feel");
    return [t1, t2, t3];
  })();
  const [factIdx, setFactIdx] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);
  useEffect(() => {
    const f = setInterval(() => setFactIdx(i => (i + 1) % aiFacts.length), 4000);
    const s = setInterval(() => setTipIdx(i => (i + 1) % styleTip.length), 5000);
    return () => { clearInterval(f); clearInterval(s); };
  }, []);

  // Garment spotlight — material if present, otherwise fall back to a
  // generic line so the slot is never empty.
  const garmentLine = productMaterial?.trim()
    || (productDescription?.trim() ? productDescription.trim().slice(0, 90) + (productDescription.trim().length > 90 ? "…" : "") : null)
    || t("Crafted with quality materials for everyday comfort");

  return (
    <div className="ps-tryon-processing-v2">
      {/* ── Left: user photo with scanning overlay ── */}
      <div className="ps-tryon-processing-v2-image">
        {previewUrl && (
          <>
            <div className="ps-tryon-processing-blur" style={{ backgroundImage: `url(${previewUrl})` }} />
            <img src={previewUrl} alt={t("Your photo")} className="ps-tryon-processing-model" />
          </>
        )}
        <div className="ps-tryon-scan-line" />
        <div className="ps-tryon-scan-overlay" />
        <div className="ps-tryon-processing-v2-badge">
          <span className="ps-tryon-processing-v2-badge-dot" aria-hidden="true" />
          {t("ANALYZING BODY MAP")}
        </div>
      </div>

      {/* ── Right: progress + content cards ── */}
      <div className="ps-tryon-processing-v2-panel">
        <div className="ps-tryon-processing-v2-head">
          <h3 className="ps-tryon-processing-v2-title">{t("Generating Your Look")}</h3>
          <p className="ps-tryon-processing-v2-sub">{t("Our AI is precisely mapping the garment to your unique proportions.")}</p>
        </div>

        <div className="ps-tryon-processing-v2-ring-wrap">
          <svg viewBox="0 0 96 96" width="120" height="120" aria-hidden="true">
            <circle cx="48" cy="48" r={RING_RADIUS} className="ps-tryon-progress-ring-track" />
            <circle
              ref={ringCb}
              cx="48" cy="48" r={RING_RADIUS}
              className="ps-tryon-progress-ring-fill"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE}
            />
          </svg>
          <div className="ps-tryon-processing-v2-ring-text">
            <span ref={pctCb} className="ps-tryon-processing-v2-pct">0%</span>
            <span ref={statusCb} className="ps-tryon-processing-v2-status">{t("Preparing your image…")}</span>
          </div>
        </div>

        {/* Hidden but kept so external ticker code keeps writing — refs need DOM nodes. */}
        <div className="ps-tryon-processing-v2-bar-hidden">
          <div ref={barCb} />
          <span ref={etaCb} />
        </div>

        <div className="ps-tryon-processing-v2-sep" />

        <div className="ps-tryon-processing-v2-section">
          <div className="ps-tryon-processing-v2-section-label">{t("WHILE YOU WAIT")}</div>
          <div className="ps-tryon-processing-v2-cards">
            {/* Style Tip */}
            <div className="ps-tryon-processing-v2-card">
              <div className="ps-tryon-processing-v2-card-icon ps-style">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c1 .9 1 1.8 1 2.3v1h6v-1c0-.5 0-1.4 1-2.3A7 7 0 0 0 12 2z" />
                </svg>
              </div>
              <div className="ps-tryon-processing-v2-card-text">
                <div className="ps-tryon-processing-v2-card-head">{t("Style Tip")}</div>
                <div className="ps-tryon-processing-v2-card-body" key={tipIdx}>{styleTip[tipIdx]}</div>
              </div>
            </div>

            {/* Garment Spotlight */}
            <div className="ps-tryon-processing-v2-card">
              <div className="ps-tryon-processing-v2-card-icon ps-spotlight">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="ps-tryon-processing-v2-card-text">
                <div className="ps-tryon-processing-v2-card-head">{t("Garment Spotlight")}</div>
                <div className="ps-tryon-processing-v2-card-body">{garmentLine}</div>
              </div>
            </div>

            {/* AI Fact */}
            <div className="ps-tryon-processing-v2-card">
              <div className="ps-tryon-processing-v2-card-icon ps-fact">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6M12 16v6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M16 12h6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" />
                </svg>
              </div>
              <div className="ps-tryon-processing-v2-card-text">
                <div className="ps-tryon-processing-v2-card-head">{t("AI Fact")}</div>
                <div className="ps-tryon-processing-v2-card-body" key={factIdx}>{aiFacts[factIdx]}</div>
              </div>
            </div>
          </div>
        </div>

        {onCancel && (
          <button type="button" className="ps-tryon-processing-v2-cancel" onClick={onCancel}>
            {t("Cancel Generation")}
          </button>
        )}
      </div>
    </div>
  );
}
